import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Mic, Send, Sparkles } from "lucide-react";
import { apiClient } from "../../services/api";
import TopBar from "../../components/layout/TopBar";
import BottomNav from "../../components/layout/BottomNav";

const STARTERS = [
  "What should I eat post-workout?",
  "High protein vegetarian meals",
  "Am I eating enough?",
  "Best breakfast for muscle gain",
];

export default function AIChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).slice(2));
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: t }]);
    setBusy(true);
    try {
      const { data } = await apiClient.post("/nutrition/chat", {
        session_id: sessionId,
        message: t,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "I couldn't reach the AI service right now. Please try again." },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-page flex flex-col" data-testid="ai-chat">
      <TopBar title="AI Nutritionist" back showProfile={false} />
      <main className="max-w-2xl mx-auto w-full flex-1 flex flex-col px-4">
        {messages.length === 0 ? (
          <div className="flex-1 flex flex-col justify-center" data-testid="empty-chat">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto rounded-full grid place-items-center" style={{ background: "var(--accent-blue-dim)" }}>
                <Sparkles size={20} style={{ color: "var(--accent-blue)" }} />
              </div>
              <h2 className="font-display text-[26px] mt-3">How can I help today?</h2>
              <p className="text-[13px] text-ink-soft mt-1">Ask me anything about Indian meals, macros, or your training diet.</p>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid="starter-prompt"
                  className="text-left bg-white rounded-[14px] p-3.5 border border-line2 transition-all hover:bg-canvas2 text-[13.5px]"
                  style={{ boxShadow: "var(--shadow-soft)" }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto py-3 space-y-3" data-testid="chat-thread">
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className="max-w-[80%] rounded-[14px] px-3.5 py-2.5 text-[14.5px] leading-relaxed"
                  style={{
                    background: m.role === "user" ? "var(--accent-blue-dim)" : "white",
                    border: m.role === "user" ? "1px solid var(--accent-blue)" : "1px solid var(--border-light)",
                    color: "var(--text-primary)",
                  }}
                  data-testid={`msg-${m.role}`}
                >
                  {m.content}
                </div>
              </motion.div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white rounded-[14px] px-3.5 py-2.5 border border-line2 text-[14px] text-ink-soft">
                  Typing<span className="animate-pulse">…</span>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
        )}

        {/* Input */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="sticky bottom-0 pb-5 pt-3 bg-canvas/95"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 90px)" }}
        >
          <div className="flex items-center gap-2 bg-white rounded-pill h-12 px-3 border border-line">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything…"
              data-testid="chat-input"
              className="flex-1 bg-transparent outline-none text-[14.5px]"
            />
            <button type="button" aria-label="Voice" className="text-ink-muted">
              <Mic size={18} />
            </button>
            <button
              type="submit"
              disabled={!input.trim() || busy}
              data-testid="chat-send"
              aria-label="Send"
              className="w-9 h-9 rounded-full grid place-items-center"
              style={{ background: "var(--accent-blue)", color: "#000" }}
            >
              <Send size={16} />
            </button>
          </div>
        </form>
      </main>
      <BottomNav />
    </div>
  );
}
